import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Drawer, IconButton, Pagination, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, TextView } from '@atoms';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import FilePresent from '@mui/icons-material/FilePresent';
import Doc from '@assets/example-core-knowledge-base.csv';
import { useDropzone } from 'react-dropzone';
import OutputIcon from '@mui/icons-material/Output';
import DeleteIcon from '@mui/icons-material/Delete';
import AddBoxIcon from '@mui/icons-material/AddBox';
import InputIcon from '@mui/icons-material/Input';
import FAQList from './faqList';
import {
  callDeleteCoreKnowledgeBase,
  callExportKnowledgeBase,
  callUploadKnowledgeBase,
  useKnowledgeBase
} from '@api';
import { toast } from 'react-toastify';
import FaqModal from './faqModal';

// interface UploadKnowledgeBaseCSVProps
interface UploadKnowledgeBaseCSVProps {
  locationId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}

const UploadKnowledgeBaseCSV: React.FC<UploadKnowledgeBaseCSVProps> = ({
  open,
  locationId,
  onClose,
  onSuccess
}: UploadKnowledgeBaseCSVProps) => {
  const [isImport, setImportOpen] = useState<boolean>(false);
  //  States
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>();
  const [error, setError] = useState<{
    title: string;
    message: string;
  }>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [openFAQModal, setOpenFAQModal] = useState<boolean>(false);
  const [operation, setOperation] = useState<'add' | 'edit' | 'delete'>('add');
  const [faqData, setFaqData] = useState<{ id: string; question?: string; answer?: string }>();
  // Funtions
  const toggleDrawer = () => {
    onClose();
  };

  const toggleOperation = (
    operationType: 'add' | 'edit' | 'delete',
    faqId?: string | undefined,
    question?: string,
    answer?: string
  ) => {
    setOperation(operationType);
    setOpenFAQModal(true);
    if (operationType === 'edit' || operationType === 'delete') {
      setFaqData((faqData) => ({
        id: faqId || '',
        question: question,
        answer: answer
      }));
    } else {
      setFaqData(undefined);
    }
  };
  const onDrop = useCallback((acceptedFiles: React.SetStateAction<File[] | undefined>) => {
    setError(undefined);
    // Do something with the files
    setAcceptedFiles(acceptedFiles);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };
  const { data, isFetching, refetch } = useKnowledgeBase({
    locationId,
    pageNumber: currentPage.toString(),
    pageSize: pageSize.toString()
  });

  const totalPages = useMemo(() => {
    if (data?.data?.totalCount && pageSize) {
      return Math.ceil(data.data.totalCount / pageSize);
    }
    return 1;
  }, [data?.data?.totalCount, pageSize]);

  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open]);

  const onSubmit = async () => {
    if (acceptedFiles && acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      const { data, code, error } = await callUploadKnowledgeBase(locationId, file);
      if (code === 200) {
        refetch();
        toast.success('Knowledge Base uploaded successfully');
        setAcceptedFiles(undefined);
        setImportOpen(false);
        setCurrentPage(1);
      } else {
        toast.error('Failed to upload knowledge base');
      }
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={toggleDrawer}>
      <Box sx={{ width: 900 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider'
          }}>
          <Typography variant="h6">Upload Knowledge Base</Typography>
          <IconButton onClick={toggleDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: '1.5rem 3rem' }}>
          {isImport && (
            <>
              <Box
                sx={{
                  border: '1px dashed #242423',
                  textAlign: 'center',
                  borderRadius: '6px',
                  paddingTop: '15%',
                  paddingBottom: '15%',
                  mt: 3
                }}
                {...getRootProps()}>
                {acceptedFiles == undefined ? (
                  <div>
                    <CloudUploadOutlined sx={{ fontSize: 40 }} />
                    <input {...getInputProps()} />
                    <TextView $fontSize={14} $fontWeight={400}>
                      Drop your file here
                    </TextView>
                  </div>
                ) : (
                  <div>
                    <FilePresent sx={{ fontSize: 40 }} />
                    <TextView>File Selected</TextView>
                  </div>
                )}
              </Box>
              <Box sx={{ mt: 1.5, mb: 2 }}>
                <TextView $fontSize={12} $fontWeight={400}>
                  Only .CSV files are accepted as uploads here. {'\n'}
                  <a
                    href={Doc}
                    download="example-core-knowledge-base.csv"
                    target="_blank"
                    rel="noreferrer">
                    Download
                  </a>{' '}
                  sample CSV template file for reference.
                </TextView>
              </Box>
              {error && <Alert severity={'error'} title={error.title} message={error.message} />}
              <Button sx={{ mt: 1 }} label={'Upload'} onClick={onSubmit} />
            </>
          )}

          <Box
            sx={{
              mt: '2rem',
              mb: '2rem',
              flexDirection: 'row',
              display: 'flex',
              gap: '1rem',
              alignItems: 'center'
            }}>
            <Button
              variant="contained"
              endIcon={<AddBoxIcon />}
              label="Add FAQ"
              onClick={() => toggleOperation('add')}
            />
            <Button
              variant="contained"
              endIcon={<OutputIcon />}
              label="Export"
              disabled={data?.data?.totalCount == 0}
              onClick={async () => {
                const { code } = await callExportKnowledgeBase(locationId);
                if (code !== 200) {
                  toast.error('Failed to export knowledge base');
                }
              }}
            />
            <Button
              variant="contained"
              endIcon={<InputIcon />}
              label="Import"
              onClick={() => setImportOpen(!isImport)}
            />
            <Button
              variant="contained"
              sx={{
                backgroundColor: 'red',
                color: '#ffffff',
                '&:hover': { backgroundColor: '#d32f2f' }
              }}
              endIcon={<DeleteIcon />}
              label="Delete FAQ's"
              onClick={() => toggleOperation('delete')}
            />
          </Box>
          {/* FAQ List */}
          {!isFetching && data?.data?.data?.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body1" color="textSecondary">
                No FAQs available for this location.
              </Typography>
            </Box>
          )}
          {isFetching && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body1" color="textSecondary">
                Loading FAQs...
              </Typography>
            </Box>
          )}

          {!isFetching &&
            data?.data?.data &&
            data.data.data.length > 0 &&
            data.data.data.map((faq) => (
              <Box key={faq.id}>
                <FAQList
                  data={faq}
                  onDelete={toggleOperation.bind(null, 'delete', faq.id)}
                  onEdit={toggleOperation.bind(null, 'edit', faq.id, faq.question, faq.answer)}
                />
              </Box>
            ))}
        </Box>
        {!isFetching && (data?.data?.totalCount ?? 0) > pageSize && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper'
            }}>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Stack>
          </Box>
        )}
      </Box>
      <FaqModal
        locationId={locationId}
        faqId={faqData?.id}
        open={openFAQModal}
        answer={faqData?.answer}
        question={faqData?.question}
        onClose={() => setOpenFAQModal(false)}
        operation={operation}
        onSuccess={(message) => {
          refetch();
          setOpenFAQModal(false);
          toast.success(message || 'FAQ added successfully');
        }}
      />
    </Drawer>
  );
};

export default UploadKnowledgeBaseCSV;
